import React from 'react';
import { Heading, PrimaryButton, TextField, SpinningLoader, useForm } from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';
import { css } from '@emotion/core';
import { sectionContainerStyles } from '../fax-test-section.styles';
import { getStatusColor, getStatusBgColor, getFaxStatusFields } from '../fax-test-section.utils';
import { FaxStatus } from '../fax-test-section.types';

interface CheckFaxStatusSectionProps {
  onCheckStatus: (faxId: string) => void;
  isLoading: boolean;
  faxStatus: FaxStatus | null;
  initialFaxId?: string;
}

export const CheckFaxStatusSection: React.FC<CheckFaxStatusSectionProps> = ({
  onCheckStatus,
  isLoading,
  faxStatus,
  initialFaxId = '',
}) => {
  const { getFieldProps, values, seedValues } = useForm({
    fields: {
      checkFaxId: { type: 'text', value: initialFaxId },
    },
  });

  // Update form if initialFaxId changes
  React.useEffect(() => {
    if (initialFaxId) {
      seedValues({ checkFaxId: initialFaxId });
    }
  }, [initialFaxId]);

  const handleCheck = () => {
    const faxId = values.checkFaxId?.trim() || '';
    onCheckStatus(faxId);
  };

  const fields = faxStatus && !faxStatus.error ? getFaxStatusFields(faxStatus) : null;

  return (
    <div css={sectionContainerStyles}>
      <Heading level={2} css={css`text-align: center !important; width: 100%;`}>
        Check Fax Status
      </Heading>

      <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(2)}; margin-top: ${theme.spacing(2)};`}>
        <TextField
          {...getFieldProps('checkFaxId')}
          label="Fax ID (check status)"
          placeholder="e.g., d7f4470f-741c-4378-84bb-5077292cc098"
          css={(theme: WeaveTheme) => css`
            input {
              min-width: 350px;
              font-size: ${theme.fontSize(14)};
            }
          `}
        />
      </div>

      <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(2)};`}>
        <PrimaryButton
          onClick={handleCheck}
          disabled={isLoading}
        >
          {isLoading ? <SpinningLoader size="small" /> : 'Refresh Status'}
        </PrimaryButton>
      </div>

      {fields && (
        <div
          css={(theme: WeaveTheme) => css`
            padding: ${theme.spacing(1.5)};
            background-color: ${getStatusBgColor(fields.status)};
            border: 1px solid ${getStatusColor(fields.status)};
            border-radius: ${theme.borderRadius.small};
          `}
        >
          <div css={(theme: WeaveTheme) => css`font-weight: ${theme.font.weight.bold}; margin-bottom: ${theme.spacing(1)};`}>
            Fax Status:
          </div>
          <div css={(theme: WeaveTheme) => css`font-size: ${theme.fontSize(14)};`}>
            <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)};`}>
              <strong>Status:</strong>{' '}
              <span
                css={(theme: WeaveTheme) => css`
                  background-color: ${getStatusColor(fields.status)};
                  color: ${theme.colors.white};
                  padding: ${theme.spacing(0.25)} ${theme.spacing(1)};
                  border-radius: ${theme.borderRadius.small};
                  font-weight: ${theme.font.weight.bold};
                `}
              >
                {fields.status}
              </span>
            </div>
            <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)};`}>
              <strong>Direction:</strong> {fields.direction}
            </div>
            <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)};`}>
              <strong>From:</strong> {fields.from}
            </div>
            <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)};`}>
              <strong>To:</strong> {fields.to}
            </div>
            {fields.createdAt && (
              <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)};`}>
                <strong>Created:</strong> {new Date(fields.createdAt).toLocaleString()}
              </div>
            )}
            {faxStatus && (faxStatus.errDesc || faxStatus.ErrDesc || faxStatus.statusDetails) && (
              <div css={(theme: WeaveTheme) => css`margin-bottom: ${theme.spacing(0.5)}; color: ${theme.colors.error};`}>
                <strong>Error:</strong> {faxStatus.errDesc || faxStatus.ErrDesc || faxStatus.statusDetails}
              </div>
            )}
          </div>
        </div>
      )}

      {faxStatus?.error && (
        <div
          css={(theme: WeaveTheme) => css`
            padding: ${theme.spacing(1.5)};
            background-color: ${theme.colors.error}10;
            border: 1px solid ${theme.colors.error};
            border-radius: ${theme.borderRadius.small};
            color: ${theme.colors.error};
          `}
        >
          {faxStatus.error}
        </div>
      )}
    </div>
  );
};
